import { setRuntimeConfig, sprintLimit } from "../src/runtime-config.js";
import { getIterations, getReviewData } from "../src/azure-devops.js";
import { createPresentation } from "../src/presentation.js";
import { runAdoJob, shouldUseGitHubActions } from "../src/github-actions-client.js";

const STORAGE_PAT = "sprint-review-ado-pat";
const STORAGE_PROXY = "sprint-review-ado-proxy";
const STORAGE_GITHUB = "sprint-review-gh-token";

const elements = {
  form: document.querySelector("#reviewForm"),
  team: document.querySelector("#team"),
  iteration: document.querySelector("#iteration"),
  sprintGoal: document.querySelector("#sprintGoal"),
  meetingNotes: document.querySelector("#meetingNotes"),
  pat: document.querySelector("#pat"),
  proxyUrl: document.querySelector("#proxyUrl"),
  githubToken: document.querySelector("#githubToken"),
  fathomButton: document.querySelector("#fathomButton"),
  previewButton: document.querySelector("#previewButton"),
  generateButton: document.querySelector("#generateButton"),
  status: document.querySelector("#formStatus"),
  preview: document.querySelector("#preview"),
  connection: document.querySelector("#connectionFields"),
};

let lastReview = null;
let iterationsById = new Map();
let fathomEnabled = false;
// En local el servidor tiene el PAT y hace todo el trabajo; en GitHub Pages
// el navegador consulta por GitHub Actions o por un proxy propio.
let useServer = false;

function setStatus(message, error = false) {
  elements.status.textContent = message;
  elements.status.classList.toggle("error", error);
}

function setBusy(busy, message = "") {
  elements.previewButton.disabled = busy;
  elements.generateButton.disabled = busy;
  if (message) setStatus(message);
}

function formatDate(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("es-CO", { dateStyle: "medium" }).format(new Date(date));
}

async function loadJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`No se pudo leer ${url}`);
  return response.json();
}

async function loadStaticConfig() {
  try {
    return await loadJson("./app-config.json");
  } catch {
    return {};
  }
}

async function loadServerConfig() {
  try {
    const response = await fetch("./api/config");
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

function applyConfig(config) {
  const teams = config.teams?.length ? config.teams : ["SWAT"];
  elements.team.innerHTML = teams
    .map((team) => `<option value="${team}">${team}</option>`)
    .join("");
  document.querySelector("#topTeam").textContent = teams[0];
  document.querySelector("#projectLabel").textContent =
    `${config.organization || "satrack-ts"} / ${config.project || "Transformation-View"}`;
  const savedPat = sessionStorage.getItem(STORAGE_PAT) || "";
  // Nunca se precarga config.proxyUrl: es una ruta relativa del servidor y el
  // campo es type="url", por lo que invalidaría el formulario.
  const savedProxy = localStorage.getItem(STORAGE_PROXY) || "";
  const savedGithub = sessionStorage.getItem(STORAGE_GITHUB) || "";
  if (elements.pat) elements.pat.value = savedPat;
  if (elements.proxyUrl) elements.proxyUrl.value = savedProxy;
  if (elements.githubToken) elements.githubToken.value = savedGithub;
  const configured = Boolean(config.serverManagedAuth || savedPat || savedGithub);
  document.querySelector("#connectionLabel").textContent = configured
    ? "Azure DevOps listo"
    : "Ingresa tu PAT";
  document.querySelector(".status-dot").classList.toggle("connected", configured);
  if (elements.connection) {
    // Un campo oculto e inválido impide enviar el formulario, así que los
    // campos de conexión se deshabilitan cuando el servidor tiene el PAT.
    const hidden = Boolean(config.serverManagedAuth);
    elements.connection.hidden = hidden;
    for (const field of [elements.pat, elements.proxyUrl, elements.githubToken]) {
      if (field) field.disabled = hidden;
    }
  }
  fathomEnabled = Boolean(config.fathomEnabled);
  useServer = Boolean(config.serverManagedAuth);
  if (elements.fathomButton) elements.fathomButton.hidden = !fathomEnabled;
  setRuntimeConfig({
    org: config.organization,
    project: config.project,
    team: teams[0],
    pat: config.serverManagedAuth ? "managed" : savedPat,
    sprintLimit: config.sprintLimit || 3,
    classificationField: config.classificationField,
    summaryField: config.summaryField,
    sprintGoalField: config.sprintGoalField,
    completedStates: config.completedStates,
    proxyUrl: savedProxy,
    serverManagedAuth: Boolean(config.serverManagedAuth),
  });
}

function syncRuntimeFromForm() {
  const pat = elements.pat?.value.trim() || "";
  const proxyUrl = elements.proxyUrl?.value.trim() || "";
  const githubToken = elements.githubToken?.value.trim() || "";
  if (pat) sessionStorage.setItem(STORAGE_PAT, pat);
  if (githubToken) sessionStorage.setItem(STORAGE_GITHUB, githubToken);
  localStorage.setItem(STORAGE_PROXY, proxyUrl);
  const serverManaged = Boolean(elements.connection?.hidden);
  setRuntimeConfig({
    team: elements.team.value,
    pat: pat || (serverManaged ? "managed" : ""),
    proxyUrl,
    githubToken,
    serverManagedAuth: serverManaged,
  });
  return { pat, proxyUrl, githubToken, serverManaged };
}

async function apiJson(url, options) {
  const response = await fetch(url, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Error ${response.status}`);
  return body;
}

async function fetchIterations() {
  if (useServer) {
    return apiJson(`./api/teams/${encodeURIComponent(elements.team.value)}/iterations`);
  }
  const { githubToken, proxyUrl, serverManaged } = syncRuntimeFromForm();
  if (shouldUseGitHubActions({ githubToken, proxyUrl, serverManagedAuth: serverManaged })) {
    return runAdoJob({ githubToken, kind: "list", team: elements.team.value });
  }
  return getIterations(elements.team.value, { limit: sprintLimit() });
}

function reviewRequest() {
  return {
    team: elements.team.value,
    iterationId: elements.iteration.value,
  };
}

async function fetchReview() {
  if (useServer) {
    return apiJson("./api/reviews/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reviewRequest()),
    });
  }
  const { githubToken, proxyUrl, serverManaged } = syncRuntimeFromForm();
  if (shouldUseGitHubActions({ githubToken, proxyUrl, serverManagedAuth: serverManaged })) {
    return runAdoJob({ githubToken, kind: "review", ...reviewRequest() });
  }
  return getReviewData(reviewRequest());
}

async function loadIterations() {
  elements.iteration.disabled = true;
  elements.iteration.innerHTML = '<option value="">Cargando sprints…</option>';
  try {
    const iterations = await fetchIterations();
    iterationsById = new Map(iterations.map((item) => [item.id, item]));
    elements.iteration.innerHTML = iterations.length
      ? iterations
          .map(
            (item) =>
              `<option value="${item.id}">${item.name}${item.timeFrame === "current" ? " · actual" : ""}</option>`,
          )
          .join("")
      : '<option value="">No se encontraron sprints</option>';
    elements.iteration.disabled = !iterations.length;
    setStatus(iterations.length ? `${iterations.length} sprints disponibles` : "");
  } catch (error) {
    elements.iteration.innerHTML = '<option value="">Configura la conexión</option>';
    setStatus(error.message, true);
  }
}

function escapeHtml(value) {
  const node = document.createElement("span");
  node.textContent = String(value ?? "");
  return node.innerHTML;
}

function renderPreview(data) {
  lastReview = data;
  document.querySelector("#previewTitle").textContent =
    `${data.team} · ${data.iteration.name}`;
  document.querySelector("#previewDates").textContent =
    `${formatDate(data.iteration.startDate)} — ${formatDate(data.iteration.finishDate)}`;
  document.querySelector("#totalItems").textContent = data.indicators.totalItems;
  document.querySelector("#planned").textContent = data.indicators.planned;
  document.querySelector("#executed").textContent = data.indicators.executed;
  document.querySelector("#itemCount").textContent =
    `${data.items.length} historias incluidas`;
  if (!elements.sprintGoal.value && data.sprintGoal) {
    elements.sprintGoal.value = data.sprintGoal;
  }
  if (!elements.meetingNotes.value && data.fathomNotes) {
    elements.meetingNotes.value = data.fathomNotes;
  }

  const classifications = Object.entries(data.indicators.classifications);
  const total = classifications.reduce((sum, [, value]) => sum + value, 0);
  document.querySelector("#classifications").innerHTML = classifications.length
    ? classifications
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => {
          const percentage = total ? Math.round((value / total) * 100) : 0;
          return `
            <div>
              <div class="bar-head"><span>${name}</span><strong>${percentage}%</strong></div>
              <div class="bar-track"><div class="bar-fill" style="width:${percentage}%"></div></div>
            </div>`;
        })
        .join("")
    : "<small>No hay esfuerzo o clasificación registrada.</small>";

  document.querySelector("#workItems").innerHTML = data.items.length
    ? data.items
        .map(
          (item) => `
            <div class="work-item">
              <span class="work-id">#${item.id}</span>
              <div class="work-title">
                <strong>${escapeHtml(item.title)}</strong>
                <small>${escapeHtml(item.parent)} · ${item.effort} pts</small>
              </div>
              <span class="state">${escapeHtml(item.state)}</span>
            </div>`,
        )
        .join("")
    : "<small>No hay historias después de aplicar las reglas de inclusión.</small>";
  elements.preview.classList.remove("hidden");
  elements.preview.scrollIntoView({ behavior: "smooth", block: "start" });
}

function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

async function loadFathomNotes() {
  const iteration = iterationsById.get(elements.iteration.value);
  if (!iteration) {
    setStatus("Selecciona un sprint antes de traer los resúmenes.", true);
    return;
  }
  elements.fathomButton.disabled = true;
  setStatus("Consultando las reuniones del sprint en Fathom…");
  try {
    const params = new URLSearchParams({
      team: elements.team.value,
      from: iteration.startDate || "",
      to: iteration.finishDate || "",
    });
    const response = await fetch(`./api/fathom/notes?${params}`);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Error ${response.status}`);
    if (!body.meetingCount) {
      setStatus(
        `No se encontraron reuniones del equipo en Fathom para este sprint (se revisaron ${body.inspected}).`,
        true,
      );
      return;
    }
    elements.meetingNotes.value = body.notes;
    setStatus(`${body.meetingCount} reuniones traídas desde Fathom.`);
  } catch (error) {
    setStatus(error.message, true);
  } finally {
    elements.fathomButton.disabled = false;
  }
}

async function preview() {
  if (!elements.form.reportValidity()) return;
  setBusy(true, "Consultando Azure DevOps y consolidando la información…");
  try {
    renderPreview(await fetchReview());
    setStatus("Datos actualizados correctamente.");
  } catch (error) {
    setStatus(error.message, true);
  } finally {
    setBusy(false);
  }
}

async function generateOnServer() {
  const response = await fetch("./api/reviews/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...reviewRequest(),
      sprintGoal: elements.sprintGoal.value.trim(),
      meetingNotes: elements.meetingNotes.value.trim(),
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Error ${response.status}`);
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] || "Sprint-Review.pptx";
  downloadBlob(await response.blob(), filename);
}

async function generateInBrowser() {
  const data =
    lastReview && lastReview.iteration?.id === elements.iteration.value
      ? lastReview
      : await fetchReview();
  renderPreview(data);
  const output = await createPresentation(data, {
    sprintGoal: elements.sprintGoal.value.trim(),
    meetingNotes: elements.meetingNotes.value.trim(),
  });
  const blob =
    output instanceof Blob
      ? output
      : new Blob([output], {
          type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        });
  const filename = `${elements.team.value}-${data.iteration.name}-Sprint-Review.pptx`.replace(
    /[^a-zA-Z0-9._-]+/g,
    "-",
  );
  downloadBlob(blob, filename);
}

async function generate(event) {
  event.preventDefault();
  if (!elements.form.reportValidity()) return;
  setBusy(true, "Generando la presentación. Esto puede tardar unos segundos…");
  try {
    if (useServer) {
      await generateOnServer();
    } else {
      await generateInBrowser();
    }
    setStatus("PowerPoint generado y descargado.");
  } catch (error) {
    setStatus(error.message, true);
  } finally {
    setBusy(false);
  }
}

async function boot() {
  const [staticConfig, serverConfig] = await Promise.all([
    loadStaticConfig(),
    loadServerConfig(),
  ]);
  applyConfig({ ...staticConfig, ...(serverConfig || {}) });
  const connected = document.querySelector(".status-dot").classList.contains("connected");
  if (connected) {
    await loadIterations();
  } else {
    elements.iteration.innerHTML = '<option value="">Ingresa un token para cargar sprints</option>';
    setStatus("En GitHub Pages usa un token de GitHub con permiso Actions.");
  }
}

elements.team.addEventListener("change", loadIterations);
elements.fathomButton?.addEventListener("click", loadFathomNotes);
elements.previewButton.addEventListener("click", preview);
elements.form.addEventListener("submit", generate);
elements.pat?.addEventListener("change", () => {
  sessionStorage.setItem(STORAGE_PAT, elements.pat.value.trim());
  loadIterations();
});
elements.githubToken?.addEventListener("change", () => {
  sessionStorage.setItem(STORAGE_GITHUB, elements.githubToken.value.trim());
  loadIterations();
});
elements.proxyUrl?.addEventListener("change", () => {
  localStorage.setItem(STORAGE_PROXY, elements.proxyUrl.value.trim());
  loadIterations();
});
boot();
